import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { Subject, combineLatest, of, zip } from 'rxjs';
import { filter, publishReplay, refCount, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { SelfServiceableAccounts } from 'src/app/feature-modules/accounts/models/accounts.model';
import { selectAccounts } from 'src/app/feature-modules/accounts/store/accounts.selector';
import { getRedirectAccountArray, getTotalAccountCount } from 'src/app/feature-modules/accounts/util/accounts.util';
import { customerAttributesActions } from 'src/app/feature-modules/customer-attributes/store/customer-attributes.actions';
import { selectCustomerAttributes } from 'src/app/feature-modules/customer-attributes/store/customer-attributes.selector';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../../services/auth.service';
import { EventTrackingService } from '../../../../services/event-tracking.service';
import { NotificationService } from '../../../../services/notification.service';
import { WindowRefService } from '../../../../services/window-ref.service';
import { selectIsRedirectedBEFHOnlyCustomer } from '../../../../store/selectors/shared-account-attribute.selector';
import { selectIsPaperlessPageEligible } from '../../../../store/selectors/shared-account-opt-in.selector';
import { waitForSourceData } from '../../../../utils/rxjs-util';
import { AccountsService } from '../../../accounts/services/accounts.service';
import { loadAccounts } from '../../../accounts/store/accounts.action';
import { getOptInActions } from '../../../opt-in/store/opt-in.actions';
import { selectOptInIsLoaded } from '../../../opt-in/store/opt-in.selectors';
import { getPaperlessActions } from '../../../profile/store/actions/profile.actions';
import { selectProfilePaperlessIsLoaded } from '../../../profile/store/selectors/profile.selectors';
import { RedirectAccount, SelfServiceableProducts } from '../../models/self-serviceable-accounts.model';
import { RedirectService } from '../../services/redirect.service';
import {
  selectHubFeatureFlags,
  selectHubIsDisplayingMaintenanceBanner,
} from '../../../../store/selectors/feature-flag.selector';
import { getHubFeatureFlagActions } from '../../../../store/actions/feature-flag.action';
import { HubCardVerificationModalComponent } from '../../../shared/components/hub-card-verification-modal/hub-card-verification-modal.component';

@Component({
    selector: 'app-redirect',
    templateUrl: './redirect.component.html',
    styleUrls: ['./redirect.component.scss'],
    standalone: false
})
export class RedirectComponent implements OnInit, OnDestroy {
  public selfServiceableAccounts: SelfServiceableAccounts;
  public idToken: string;
  public isCallbackParam = false;
  public cid: string;
  public callbackDomain: string;
  private readonly window;

  readonly ngUnsubscribe$: Subject<boolean> = new Subject();
  accounts$ = this.store.select(selectAccounts).pipe(publishReplay(1), refCount());
  displayMaintenanceBanner$ = waitForSourceData(
    [this.store.select(selectHubFeatureFlags)],
    this.store.select(selectHubIsDisplayingMaintenanceBanner)
  );
  isPaperlessPageEligible$ = waitForSourceData(
    [this.store.select(selectOptInIsLoaded), this.store.select(selectProfilePaperlessIsLoaded)],
    this.store.select(selectIsPaperlessPageEligible)
  ).pipe(takeUntil(this.ngUnsubscribe$));
  isRedirectedBEFHOnlyCustomer$ = waitForSourceData(
    [this.store.select(selectCustomerAttributes), this.accounts$],
    this.store.select(selectIsRedirectedBEFHOnlyCustomer)
  ).pipe(takeUntil(this.ngUnsubscribe$));

  constructor(
    private readonly store: Store,
    private readonly redirectService: RedirectService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly accountsService: AccountsService,
    private readonly windowRefService: WindowRefService,
    private readonly eventTrackingService: EventTrackingService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly cookieService: CookieService,
    private readonly authService: AuthService
  ) {
    this.window = windowRefService.nativeWindow;
  }

  async ngOnInit() {
    this.activatedRoute.queryParams.pipe(filter((params) => params.callback)).subscribe((params) => {
      this.callbackDomain = params.callback.toLowerCase();
      // if there is a callback domain, set isCallbackParam so markup knows not to render unneeded elements just before redirect occurs
      this.isCallbackParam = !!this.callbackDomain;
    });

    const { cid, idToken } = await this.authService.handleCurrentSessionRetrieval();
    this.cid = cid;
    this.idToken = idToken;

    this.store.dispatch(customerAttributesActions.start({ request: {} }));
    this.store.dispatch(loadAccounts());
    this.store.dispatch(getOptInActions.start({ request: null }));
    this.store.dispatch(getHubFeatureFlagActions.start({ request: 'hub' }));

    combineLatest([this.accounts$.pipe(filter((accounts) => !!accounts)), this.displayMaintenanceBanner$])
      .pipe(
        filter((accounts) => !!accounts),
        take(1),

        switchMap(([accounts, displayMaintenanceBanner]) => {
          let data$;
          if (
            accounts.loanAccounts[0]?.accountDetailsList.length > 0 &&
            accounts.loanAccounts[0].url &&
            !displayMaintenanceBanner
          ) {
            const customerId = new URL(accounts.loanAccounts[0].url).searchParams.get('domain');
            this.store.dispatch(getPaperlessActions.start({ request: parseInt(customerId) }));
            data$ = zip(
              this.isRedirectedBEFHOnlyCustomer$,
              this.isPaperlessPageEligible$,
              this.accounts$.pipe(filter((accounts) => !!accounts))
            );
          } else {
            data$ = zip(
              this.isRedirectedBEFHOnlyCustomer$,
              of(false),
              this.accounts$.pipe(filter((accounts) => !!accounts))
            );
          }
          return data$;
        })
      )
      .subscribe(([isRedirectedBEFHOnlyCustomer, isPaperlessPageEligible, accounts]) => {
        // Account loading error
        if (getTotalAccountCount(accounts) === 0) {
          this.notificationService.displayServiceError(
            'Sorry there appears to be an issue. ' +
              'Please contact customer service at 855-282-6353 if you have a loan product or 833-707-1226 ' +
              'if you have a credit card product.'
          );
          return;
        }

        //Assign values
        this.selfServiceableAccounts = accounts;
        this.redirectService.dropIsBEFHOnlyCustomerToCookie(isRedirectedBEFHOnlyCustomer);

        //Redirect for befh only customer
        if (isRedirectedBEFHOnlyCustomer) {
          return this.accountsService.redirectBefhOnlyUser(accounts.bechAccounts[0]);
        }

        //Set cookies + localStorage vals for applications
        this.setUserApplicationData(accounts);

        if (this.callbackDomain) {
          this.getCallBackAccount(
            getRedirectAccountArray(this.selfServiceableAccounts),
            this.callbackDomain,
            isPaperlessPageEligible
          );
          return;
        }

        // navigate to paperless enroll page if relevant to customer
        if (isPaperlessPageEligible) {
          return this.router.navigate(['/redirect/paperless-enrollment']);
        }

        const params = this.activatedRoute.snapshot?.queryParams;

        if (params?.destination) {
          this.deepLinkToCsw(accounts, params);
        }

        // if no accounts error and no callback domain, nav to customer hub
        this.router.navigate(['/customer-hub']);
      });
  }

  deepLinkToCsw(accounts, params) {
    const baseUrl = accounts.loanAccounts?.[0]?.url;
    if (!baseUrl) {
      console.error('No loan account URL available for CSW redirect');
      return;
    }

    if (params.id) {
      const hasMatchingAccount = accounts.loanAccounts?.[0]?.accountDetailsList?.some(
        (account) => account.id === parseInt(params.id)
      );

      if (hasMatchingAccount) {
        const cswUrl = `${baseUrl}&destination=${params.destination}&id=${params.id?.trim()}`;
        this.windowRefService.nativeWindow.location.href = cswUrl;
        return;
      } else {
        console.warn(`Account with id ${params.id} not found`);
        return;
      }
    } else {    
      const cswUrl = `${baseUrl}&destination=${encodeURIComponent(params.destination)}`;
      this.windowRefService.nativeWindow.location.href = cswUrl;
      return;
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }

  setUserApplicationData(accounts: SelfServiceableAccounts) {
    const date365IntoFuture = new Date();
    date365IntoFuture.setDate(date365IntoFuture.getDate() + 365);

    const loanAccount = accounts?.loanAccounts[0];
    this.cookieService.set(
      'has_bestegg_loan',
      `${!!loanAccount}`,
      date365IntoFuture,
      '/',
      environment.mainDomain,
      environment.production,
      'Strict'
    );
    if (loanAccount && loanAccount?.url) {
      localStorage.setItem('bestegg_loan_url', loanAccount.url);
    }

    // we're only interested in data from first card account
    const creditCardAccount = accounts?.cardAccounts[0];
    this.cookieService.set(
      'has_bestegg_card',
      `${!!creditCardAccount}`,
      date365IntoFuture,
      '/',
      environment.mainDomain,
      environment.production,
      'Strict'
    );
    if (creditCardAccount) {
      if (accounts?.cardAccounts.length === 1) {
        // Persist only a derived boolean flag (never account data itself) so
        // there is no clear-text sensitive information in localStorage.
        const isSecondLevelValidated = creditCardAccount.secondLevelValidated === true;
        localStorage.setItem(
          'bestegg_credit_card_session',
          JSON.stringify({
            secondLevelValidated: isSecondLevelValidated,
          })
        );
      } else {
        localStorage.removeItem('bestegg_credit_card_session');
      }
    }
  }

  onRedirectToAccount(redirectAccount: RedirectAccount, isPaperlessPageEligible: boolean) {
    if (redirectAccount.type === SelfServiceableProducts.LOAN) {
      this.window.FS.event('[Auth] Personal Loan Login');
      this.eventTrackingService.trackEnterpriseEvent({ eventName: 'redirectToLoanSite', cid: this.cid });
      this.window.location.href = redirectAccount.url;
    } else if (redirectAccount.type === SelfServiceableProducts.BECH) {
      if (isPaperlessPageEligible) {
        return this.router.navigate(['/redirect/paperless-enrollment'], {
          state: { isBefhCallbackDomain: true },
        });
      }
      this.accountsService.navigateToBefhCallbackDomain(redirectAccount, this.cid);
    } else if (redirectAccount.type === SelfServiceableProducts.CARD) {
      this.window.FS.event('[Auth] Credit Card Login');
      if (redirectAccount.secondLevelValidated) {
        this.eventTrackingService.trackEnterpriseEvent({ eventName: 'redirectToCreditCardSite', cid: this.cid });
        this.router.navigateByUrl('/redirect/saml2', {
          state: {
            saml: redirectAccount.saml,
            secondLevelValidated: redirectAccount.secondLevelValidated,
          },
        });
      } else {
        this.eventTrackingService.trackEnterpriseEvent({ eventName: 'creditCard2ndLevelVerification', cid: this.cid });
        this.loadTokenExModal(redirectAccount, this.idToken);
      }
    } else {
      console.error('unknown product type');
    }
  }

  getCallBackAccount(selfServiceableAccounts: RedirectAccount[], domainName: string, isPaperlessPageEligible: boolean) {
    selfServiceableAccounts.forEach((account) => {
      if (account.type === domainName) {
        this.onRedirectToAccount(account, isPaperlessPageEligible);
      }
    });
  }

  loadTokenExModal(redirectAccount: RedirectAccount, idToken: string) {
    this.dialog.open(HubCardVerificationModalComponent, {
      data: { redirectAccount, idToken },
    });
  }
}
